import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope, FaBuilding, FaClock, FaShieldHalved } from 'react-icons/fa6';
import './TermsAndPrivacy.css';

const PrivacyPolicy = () => {
    return (
        <div className="terms-privacy-container">
            <header className="policy-header">
                <h1>PRIVACY POLICY</h1>
                <p className="company-name">EdVergenceX Private Limited</p>
                <div className="policy-dates">
                    <span>Last Updated: February 01, 2026</span>
                    <span className="divider">|</span>
                    <span>Effective Date: February 01, 2026</span>
                </div>
            </header>

            <div className="terms-privacy-content">
                <section className="contact-quick-access">
                    <h3><FaEnvelope style={{ marginRight: '8px' }} /> Quick Contact</h3>
                    <div className="contact-grid">
                        <div className="contact-item">
                            <strong>Privacy Inquiries:</strong>
                            <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a>
                        </div>
                        <div className="contact-item">
                            <strong>Grievance Officer:</strong>
                            <a href="mailto:grievance@EdVergenceX.in">grievance@EdVergenceX.in</a>
                        </div>
                        <div className="contact-item full-width">
                            <strong><FaBuilding style={{ marginRight: '8px' }} /> Registered Office:</strong>
                            <p>No. 67, 3rd Main San, R.m.v. Extension Ii Stage, Bangalore, Bangalore North, Karnataka, India, 560094</p>
                        </div>
                        <div className="contact-item full-width">
                            <strong><FaClock style={{ marginRight: '8px' }} /> Response Time:</strong>
                            <p>We respond to all privacy requests within 90 days.</p>
                        </div>
                    </div>
                </section>

                <section className="privacy-glance">
                    <h3><FaShieldHalved style={{ marginRight: '8px' }} /> Privacy at a Glance</h3>
                    <div className="glance-table-wrapper">
                        <table className="glance-table">
                            <tbody>
                                <tr>
                                    <th>Who We Are</th>
                                    <td>EdVergenceX connects Higher Education Institutions (HEIs) with industry experts (Professors of Practice) to deliver professional courses and programs.</td>
                                </tr>
                                <tr>
                                    <th>What We Collect</th>
                                    <td>Professional credentials, academic records, usage data, and payment information needed to operate our platform.</td>
                                </tr>
                                <tr>
                                    <th>How We Use It</th>
                                    <td>To match experts with institutions, facilitate course delivery, process payments, improve our services, and ensure platform security.</td>
                                </tr>
                                <tr>
                                    <th>Who We Share With</th>
                                    <td>HEIs, Professors of Practice, students (as needed for course delivery on need to know basis), service providers, and legal authorities when required.</td>
                                </tr>
                                <tr>
                                    <th>Your Control</th>
                                    <td>Access, correct, delete, or download your data. Opt-out of marketing. Withdraw consent anytime.</td>
                                </tr>
                                <tr>
                                    <th>How to Contact</th>
                                    <td><a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a> for all privacy-related questions and requests.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <nav className="table-of-contents">
                    <h3>Table of Contents</h3>
                    <ol>
                        <li><a href="#intro">Introduction & Scope</a></li>
                        <li><a href="#collection">Information We Collect</a></li>
                        <li><a href="#usage">How We Use Your Information</a></li>
                        <li><a href="#rights">Your Privacy Rights</a></li>
                        <li><a href="#sharing">Information Sharing</a></li>
                        <li><a href="#security">Data Security</a></li>
                        <li><a href="#retention">Data Retention</a></li>
                        <li><a href="#cookies">Cookies & Tracking</a></li>
                        <li><a href="#third-party">Third-Party Services</a></li>
                        <li><a href="#international">International Transfers</a></li>
                        <li><a href="#children">Children's Privacy</a></li>
                        <li><a href="#legal">Legal Compliance</a></li>
                        <li><a href="#changes">Changes to This Policy</a></li>
                        <li><a href="#contact">Contact & Grievance Redressal</a></li>
                    </ol>
                </nav>

                <section id="intro">
                    <h2>1. Introduction & Scope</h2>
                    <p>EdVergenceX Private Limited ("EdVergenceX," "we," "us," or "our") operates an educational technology platform that connects Higher Education Institutions (“HEI” or “HEIs”) with industry professionals (“Professors of Practice” or “POP”) to deliver courses, programs, and industry-integrated learning experiences.</p>

                    <h3>Who This Policy Covers</h3>
                    <p>This Privacy Policy applies to:</p>
                    <ul>
                        <li><strong>Professors of Practice (POPs):</strong> Industry experts who create and deliver courses</li>
                        <li><strong>Higher Education Institutions (HEIs):</strong> Universities, colleges, and educational organizations</li>
                        <li><strong>Students:</strong> Learners enrolled in courses through their institutions</li>
                        <li><strong>Visitors:</strong> Anyone accessing our website or platform</li>
                    </ul>

                    <h3>Your Consent</h3>
                    <p>By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy and our Terms of Service. If you do not agree, please discontinue use of our platform.</p>
                </section>

                <section id="collection">
                    <h2>2. Information We Collect</h2>

                    <h3>2.1 Information from Professors of Practice</h3>
                    <h4>Profile & Credentials</h4>
                    <ul>
                        <li><strong>Personal identifiers:</strong> Full name, email address, phone number, postal address</li>
                        <li><strong>Professional information:</strong> Educational qualifications, degrees, certifications, work experience, employment history, areas of expertise, industry specializations</li>
                        <li><strong>Identity documents:</strong> Government-issued ID, PAN card, Aadhaar (for verification), educational certificates, professional licenses</li>
                        <li><strong>Professional media:</strong> CV/resume, profile photo, professional bio, publications, achievements</li>
                    </ul>

                    <h4>Financial Information</h4>
                    <ul>
                        <li>Bank account details (for payment processing)</li>
                        <li>PAN number and GSTIN (for tax compliance)</li>
                        <li>Payment history and transaction records</li>
                    </ul>

                    <h4>Content & Activity</h4>
                    <ul>
                        <li><strong>Course materials:</strong> Lecture content, presentations, videos, assignments, assessments, reading materials</li>
                        <li><strong>Communications:</strong> Messages with HEIs and students, support inquiries, feedback</li>
                        <li><strong>Platform activity:</strong> Login times, content uploads, course management actions</li>
                    </ul>

                    <h3>2.2 Information from Higher Education Institutions</h3>
                    <h4>Institutional Profile</h4>
                    <ul>
                        <li>Organization name, type, and accreditation details</li>
                        <li>Registered address and contact information</li>
                        <li><strong>Authorized representatives:</strong> Names, designations, email addresses, phone numbers of institutional contacts</li>
                    </ul>

                    <h4>Account & Commercial Data</h4>
                    <ul>
                        <li>Login credentials and account settings</li>
                        <li>Subscription plan details and payment information</li>
                        <li>Course requests, enrollment data, and usage statistics</li>
                        <li>Student data provided by institutions (subject to Data Processing Agreements)</li>
                    </ul>

                    <h4>Institutional Activity</h4>
                    <ul>
                        <li>Course management actions, instructor evaluations, platform usage patterns</li>
                        <li>Communication with POPs and support team</li>
                    </ul>

                    <h3>2.3 Information from Students</h3>
                    <h4>Student Profile</h4>
                    <ul>
                        <li><strong>Personal identifiers:</strong> Name, email address, student ID/enrollment number</li>
                        <li>Institutional affiliation and program details</li>
                    </ul>

                    <h4>Academic Records</h4>
                    <ul>
                        <li>Course enrollments and registration data</li>
                        <li>Attendance records and participation logs</li>
                        <li>Assignments, submissions, and assessments</li>
                        <li>Grades, scores, and performance metrics</li>
                        <li>Course completion status and certificates earned</li>
                        <li>Learning progress and activity tracking</li>
                    </ul>

                    <h4>Communications & Feedback</h4>
                    <ul>
                        <li>Messages to instructors and support team</li>
                        <li>Course reviews and feedback</li>
                        <li>Discussion forum posts and peer interactions</li>
                    </ul>

                    <h3>2.4 Automatically Collected Information</h3>
                    <h4>Technical Data</h4>
                    <ul>
                        <li>IP address and geolocation (city/region level)</li>
                        <li><strong>Device information:</strong> Type, model, operating system, browser type and version</li>
                        <li>Unique device identifiers and mobile network information</li>
                    </ul>

                    <h4>Usage & Analytics Data</h4>
                    <ul>
                        <li>Pages viewed, features accessed, and navigation paths</li>
                        <li>Time spent on platform, session duration, and frequency of visits</li>
                        <li>Search queries, clicks, and interaction patterns</li>
                        <li>Referral sources and campaign tracking data</li>
                    </ul>

                    <h4>Cookies & Tracking Technologies</h4>
                    <p>Session cookies, persistent cookies, web beacons, pixel tags. See Section 8 for detailed cookie information.</p>
                </section>

                <section id="usage">
                    <h2>3. How We Use Your Information</h2>

                    <h3>3.1 Core Platform Services</h3>
                    <h4>Marketplace Operations</h4>
                    <ul>
                        <li>Onboard and verify Professor of Practice credentials</li>
                        <li>Create and maintain professional profiles for POPs</li>
                        <li>Match POPs with HEIs based on expertise, availability, and institutional requirements</li>
                        <li>Facilitate course proposals, negotiations, and agreements</li>
                    </ul>

                    <h4>Course Delivery</h4>
                    <ul>
                        <li>Enable course creation, content hosting, and distribution</li>
                        <li>Manage student enrollments and access permissions</li>
                        <li>Track attendance, assignments, and assessments</li>
                        <li>Generate progress reports and performance analytics</li>
                        <li>Issue certificates and completion credentials</li>
                    </ul>

                    <h4>Payment Processing</h4>
                    <ul>
                        <li>Process payments to Professors of Practice</li>
                        <li>Collect subscription fees from HEIs</li>
                        <li>Generate invoices and maintain financial records</li>
                        <li>Comply with tax obligations (TDS, GST)</li>
                    </ul>

                    <h4>Account Management</h4>
                    <ul>
                        <li>Authenticate user identities and maintain account security</li>
                        <li>Provide customer support and respond to inquiries</li>
                        <li>Send transactional notifications about account activity</li>
                    </ul>

                    <h3>3.2 Platform Improvement & Development</h3>
                    <ul>
                        <li>Analyze usage patterns to enhance user experience</li>
                        <li>Develop new features and services based on user needs</li>
                        <li>Conduct A/B testing and product experiments</li>
                        <li>Optimize platform performance and fix technical issues</li>
                        <li>Generate anonymized insights about learning outcomes</li>
                    </ul>

                    <h3>3.3 Communication & Marketing</h3>
                    <h4>Transactional Communications (cannot opt-out)</h4>
                    <ul>
                        <li>Account verification and security alerts</li>
                        <li>Course schedules, assignments, and deadlines</li>
                        <li>Payment confirmations and invoices</li>
                        <li>Terms and policy updates</li>
                    </ul>

                    <h4>Marketing Communications (can opt-out)</h4>
                    <ul>
                        <li>Platform updates and new feature announcements</li>
                        <li>Course recommendations and promotional offers</li>
                        <li>Educational content, webinars, and events</li>
                        <li>Surveys and feedback requests</li>
                    </ul>

                    <h3>3.4 Security & Fraud Prevention</h3>
                    <ul>
                        <li>Monitor for suspicious activity and unauthorized access</li>
                        <li>Detect and prevent fraud, spam, and abuse</li>
                        <li>Verify identity for high-value transactions</li>
                        <li>Investigate security incidents and policy violations</li>
                    </ul>

                    <h3>3.5 Legal & Compliance</h3>
                    <ul>
                        <li>Comply with applicable laws and regulations</li>
                        <li>Respond to legal requests, court orders, and regulatory inquiries</li>
                        <li>Enforce our Terms of Service and platform policies</li>
                        <li>Protect our rights, property, and safety, and that of our users</li>
                        <li>Resolve disputes and investigate complaints</li>
                    </ul>

                    <h3>3.6 Research & Analytics</h3>
                    <ul>
                        <li>Conduct educational research using anonymized or aggregated data</li>
                        <li>Generate industry reports and benchmarking studies</li>
                        <li>Contribute to academic research (with appropriate approvals)</li>
                        <li>Evaluate program effectiveness and learning outcomes</li>
                    </ul>

                    <h3>Legal Basis for Processing</h3>
                    <p>We process your personal information based on:</p>
                    <ul>
                        <li><strong>Contractual necessity:</strong> To provide services you've requested</li>
                        <li><strong>Consent:</strong> For optional activities like marketing communications</li>
                        <li><strong>Legitimate interests:</strong> For platform improvement, security, and business operations</li>
                        <li><strong>Legal obligations:</strong> To comply with applicable laws and regulations</li>
                    </ul>
                </section>

                <section id="rights">
                    <h2>4. Your Privacy Rights</h2>
                    <p>You have significant control over your personal information. Depending on your location and applicable laws, you may exercise the following rights:</p>

                    <h3>4.1 Access & Transparency Rights</h3>
                    <p><strong>Right to Access:</strong> Request a copy of all personal information we hold about you. Receive data in a structured, commonly used format (data portability). Understand how your data is being used and who it's shared with.</p>
                    <p><em>How to Exercise:</em> Email <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a> with subject line "Access Request"</p>

                    <h3>4.2 Correction & Update Rights</h3>
                    <p><strong>Right to Correction:</strong> Update inaccurate or incomplete personal information. Modify profile details, professional credentials, and preferences. Correct errors in academic records (subject to institutional policies).</p>
                    <p><em>How to Exercise:</em> Update information directly through your account settings, or contact <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a> for assistance</p>

                    <h3>4.3 Deletion & Erasure Rights</h3>
                    <p><strong>Right to Deletion:</strong> Request deletion of your personal information. Close your account and remove your profile. Erase data that is no longer necessary for stated purposes.</p>
                    <p><strong>Important Limitations:</strong> We may retain data required for legal compliance (financial records, tax documents), academic records per institutional and regulatory requirements, and anonymized or aggregated data used for research.</p>
                    <p><em>How to Exercise:</em> Email <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a> with subject line "Deletion Request"</p>

                    <h3>4.4 Control & Restriction Rights</h3>
                    <p><strong>Right to Restrict Processing:</strong> Limit how we use your information while disputes are resolved. Object to processing based on legitimate interests. Restrict use for direct marketing purposes.</p>
                    <p><strong>Right to Object:</strong> Object to data processing for direct marketing (absolute right). Object to automated decision-making or profiling. Challenge processing based on legitimate interests.</p>
                    <p><em>How to Exercise:</em> Email <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a> or use opt-out links in marketing emails</p>

                    <h3>4.5 Consent Withdrawal</h3>
                    <p><strong>Right to Withdraw Consent:</strong> Withdraw consent for any processing activity based on consent. Opt-out of marketing communications at any time. Disable cookies through browser settings (may affect functionality).</p>

                    <h3>4.6 Complaint & Redressal Rights</h3>
                    <p><strong>Right to Complain:</strong> Lodge complaints with our Grievance Officer. Escalate concerns to relevant data protection authorities. Seek legal remedies for privacy violations.</p>
                    <p><em>How to Complain:</em> Contact <a href="mailto:grievance@EdVergenceX.in">grievance@EdVergenceX.in</a></p>

                    <h3>Exercising Your Rights</h3>
                    <ul>
                        <li><strong>Response Timeline:</strong> We will respond to all requests within 90 days of receipt</li>
                        <li><strong>Verification Required:</strong> We may request additional information to verify your identity before processing requests</li>
                        <li><strong>No Fee:</strong> Exercising your privacy rights is free of charge, unless requests are manifestly unfounded or excessive</li>
                        <li><strong>For Students:</strong> Some rights may be subject to your institution's policies, especially regarding academic records</li>
                    </ul>
                </section>

                <section id="sharing">
                    <h2>5. Information Sharing</h2>
                    <p>We share personal information only as described below. We do not sell your personal data to third parties.</p>

                    <h3>5.1 Sharing Within the Platform</h3>
                    <p><strong>What HEIs Can See:</strong> Complete POP professional profiles, course content, student performance data, attendance records, and aggregate performance metrics.</p>
                    <p><strong>What POPs Can See:</strong> HEI requirements, student names/IDs (for enrolled students), student submissions, enrollment numbers, and feedback.</p>
                    <p><strong>What Students Can See:</strong> POP professional profiles, course syllabus/materials, their own records, and peer discussion forums.</p>
                    <p><strong>What Remains Private:</strong> Financial information, identity verification documents, private messages, and personal contact information (unless explicitly shared).</p>

                    <h3>5.2 Service Providers & Business Partners</h3>
                    <div className="glance-table-wrapper">
                        <table className="glance-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Partners</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Payment Processors</td>
                                    <td>Razorpay, PayU, Stripe, PayPal</td>
                                    <td>Payment processing, transaction management, fraud prevention</td>
                                </tr>
                                <tr>
                                    <td>Cloud Infrastructure</td>
                                    <td>AWS, Google Cloud, Microsoft Azure</td>
                                    <td>Data storage, hosting, content delivery</td>
                                </tr>
                                <tr>
                                    <td>Communication</td>
                                    <td>SendGrid, Twilio, Amazon SES</td>
                                    <td>Email delivery, SMS notifications, push notifications</td>
                                </tr>
                                <tr>
                                    <td>Analytics</td>
                                    <td>Google Analytics, Mixpanel, Hotjar</td>
                                    <td>Usage analysis, performance monitoring, behavior insights</td>
                                </tr>
                                <tr>
                                    <td>Support</td>
                                    <td>Zendesk, Freshdesk, Intercom</td>
                                    <td>Support ticket management, live chat</td>
                                </tr>
                                <tr>
                                    <td>Verification</td>
                                    <td>Third-party KYC services</td>
                                    <td>Identity verification, credential validation</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>5.3 Legal & Regulatory Disclosures</h3>
                    <p>We may disclose information for legal obligations (court orders, tax compliance), rights protection (enforcing terms, investigating fraud), and public safety (preventing harm, law enforcement cooperation).</p>

                    <h3>5.4 Business Transfers</h3>
                    <p>In the event of a merger, acquisition, or sale of assets, your personal information may be transferred to the acquiring entity. We will notify you and ensure they honor this policy.</p>

                    <h3>5.5 With Your Consent</h3>
                    <p>We may share information with your explicit consent for case studies, testimonials, or third-party integrations you authorize.</p>
                </section>

                <section id="security">
                    <h2>6. Data Security</h2>
                    <p>We implement comprehensive technical, administrative, and physical security measures to protect your personal information.</p>

                    <h3>6.1 Technical Safeguards</h3>
                    <ul>
                        <li><strong>Encryption:</strong> TLS 1.2+ in transit, AES-256 at rest, encrypted backups.</li>
                        <li><strong>Access Controls:</strong> Role-based access (RBAC), Multi-factor authentication (MFA), strong password requirements.</li>
                        <li><strong>Network Security:</strong> Firewalls, intrusion detection (IDS/IPS), DDoS protection.</li>
                        <li><strong>Application Security:</strong> Secure coding, vulnerability scanning, protection against OWASP Top 10.</li>
                    </ul>

                    <h3>6.2 Administrative Safeguards</h3>
                    <ul>
                        <li>Comprehensive information security policies and handling guidelines.</li>
                        <li>Regular staff training and confidentiality agreements.</li>
                        <li>Vendor due diligence and contractual data protection obligations.</li>
                    </ul>

                    <h3>6.3 Physical Safeguards</h3>
                    <ul>
                        <li>Restricted data center access with biometric controls and 24/7 surveillance.</li>
                        <li>Encrypted devices with remote wipe capabilities and secure hardware disposal.</li>
                    </ul>

                    <h3>6.4 Monitoring & Response</h3>
                    <ul>
                        <li>Continuous real-time security monitoring and automated threat detection.</li>
                        <li>Dedicated incident response team and breach notification protocols.</li>
                    </ul>

                    <p><strong>Reporting Security Issues:</strong> If you discover a vulnerability, please report it to <a href="mailto:security@EdVergenceX.in">security@EdVergenceX.in</a></p>
                </section>

                <section id="retention">
                    <h2>7. Data Retention</h2>
                    <div className="glance-table-wrapper">
                        <table className="glance-table">
                            <thead>
                                <tr>
                                    <th>Data Category</th>
                                    <th>Retention Period</th>
                                    <th>Justification</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Active Accounts</td>
                                    <td>Duration of activity</td>
                                    <td>Service provision</td>
                                </tr>
                                <tr>
                                    <td>Inactive Accounts</td>
                                    <td>2 years after last login</td>
                                    <td>Grace period for reactivation</td>
                                </tr>
                                <tr>
                                    <td>Academic Records</td>
                                    <td>5-7 years after completion</td>
                                    <td>Educational regulations</td>
                                </tr>
                                <tr>
                                    <td>Financial Records</td>
                                    <td>7 years</td>
                                    <td>Income Tax Act compliance</td>
                                </tr>
                                <tr>
                                    <td>Security Logs</td>
                                    <td>1-3 years</td>
                                    <td>Monitoring and investigation</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section id="cookies">
                    <h2>8. Cookies & Tracking</h2>
                    <p>We use cookies and similar technologies (web beacons, pixels, local storage) to enhance experience, analyze usage, and deliver relevant content.</p>

                    <h3>8.1 Types of Cookies</h3>
                    <ul>
                        <li><strong>Strictly Necessary:</strong> Essential for platform functionality and security.</li>
                        <li><strong>Functional:</strong> Remember your preferences and settings.</li>
                        <li><strong>Analytics:</strong> Understand usage and improve performance (Google Analytics, Mixpanel).</li>
                        <li><strong>Marketing:</strong> Deliver relevant ads (requires your consent).</li>
                    </ul>
                </section>

                <section id="third-party">
                    <h2>9. Third-Party Services</h2>
                    <p>Our platform integrates with video conferencing (Zoom, Meet, Teams), content repositories (Drive, Dropbox), and SSO providers (Google, Microsoft, LinkedIn). We are not responsible for their privacy practices.</p>
                </section>

                <section id="international">
                    <h2>10. International Transfers</h2>
                    <p>Our primary processing is in India. Cross-border transfers (to U.S., EU, Singapore) use safeguards like Standard Contractual Clauses (SCCs) and encryption.</p>
                </section>

                <section id="children">
                    <h2>11. Children's Privacy</h2>
                    <p>The platform is for adult learners. Any access by users under 18 requires institutional authorization. We implement enhanced protections for such users, including no targeted advertising.</p>
                </section>

                <section id="legal">
                    <h2>12. Legal Compliance</h2>
                    <p>We comply with the IT Act 2000, DPDP Act 2023, Income Tax Act 1961, and GST Act, as well as international regulations like GDPR and CCPA where applicable.</p>
                </section>

                <section id="changes">
                    <h2>13. Changes to This Policy</h2>
                    <p>Material changes will be notified via email or platform banner at least 30 days before the effective date. Minor updates are effective immediately upon posting.</p>
                </section>

                <section id="contact">
                    <h2>14. Contact & Grievance Redressal</h2>
                    <div className="contact-details-box">
                        <p><strong>Privacy Inquiries:</strong> <a href="mailto:privacy@EdVergenceX.in">privacy@EdVergenceX.in</a></p>
                        <p><strong>Grievance Officer:</strong> <a href="mailto:grievance@EdVergenceX.in">grievance@EdVergenceX.in</a></p>
                        <p><strong>Registered Office:</strong> No. 67, 3rd Main San, R.m.v. Extension Ii Stage, Bangalore, Bangalore North, Karnataka, India, 560094</p>
                    </div>
                </section>

                <div className="back-button-container">
                    <Link to="/" className="back-button">
                        <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
